import { Clock, FileDown, Share2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/shared/ui/button';
import { Logo } from '@/shared/ui/logo';
import type { CaseSummary, PatientLinkView } from '@/types';

/**
 * Who sent this, what it is, and how long the link lives.
 *
 * The chips carry what a dentist checks before looking at anything else - the
 * right tooth, the right material, the right shade. No patient name, only the
 * lab's reference: a link can be forwarded, and the case identity should not
 * travel with it.
 */
function daysLeft(iso: string): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000));
}

function Chip({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex shrink-0 flex-col rounded-lg border border-border bg-card px-2.5 py-1 leading-tight">
      <span className="text-[10px] font-medium tracking-wide text-muted-foreground uppercase">
        {label}
      </span>
      <span className={cn('text-[13px] font-semibold text-foreground', mono && 'font-mono')}>
        {value}
      </span>
    </div>
  );
}

interface CaseHeaderProps {
  summary: CaseSummary;
  /** No button without it. */
  onSharePatient?: () => void;
  /** Once made, the button becomes the way back to it - and shows the patient's answer. */
  patientLink: PatientLinkView | null;
  /** No button without it. */
  onReport?: () => void;
}

/** A dot on the patient-link button: what the patient said about the fitting date. */
function answerTone(link: PatientLinkView): { dot: string; title: string } {
  if (!link.active) return { dot: 'bg-neutral-400', title: 'Patient link turned off' };
  const status = link.appointment?.status;
  if (status === 'confirmed') return { dot: 'bg-success-500', title: 'Patient confirmed the date' };
  if (status === 'reschedule_requested') {
    return { dot: 'bg-warning-500', title: 'Patient asked for another time' };
  }
  if (status === 'pending') return { dot: 'bg-neutral-400', title: 'Waiting for the patient' };
  return { dot: 'bg-primary', title: 'Patient link is open' };
}

export function CaseHeader({ summary, onSharePatient, patientLink, onReport }: CaseHeaderProps) {
  const days = daysLeft(summary.expiresAt);
  const materials = [...new Set(summary.teeth.map((tooth) => tooth.material))].join(', ');
  const shades =
    [...new Set(summary.teeth.map((tooth) => tooth.shade).filter(Boolean))].join(', ') || '—';
  const restorations = [...new Set(summary.teeth.map((tooth) => tooth.restoration))].join(', ');

  return (
    <header className="flex flex-col gap-3 border-b border-border bg-background px-4 py-3 lg:flex-row lg:items-center lg:gap-5">
      <div className="flex items-center gap-4">
        <Logo />
        <span className="hidden h-8 w-px bg-border lg:block" />
        <div className="min-w-0">
          <h1 className="text-[15px] leading-tight font-semibold">Crown design for review</h1>
          <p className="truncate text-[12.5px] text-muted-foreground">
            From {summary.labName} · designed by {summary.designedBy}
          </p>
        </div>
      </div>

      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 lg:mx-0 lg:px-0 lg:pb-0">
        <Chip label="Patient ID" value={summary.patientRef} mono />
        <Chip
          label={summary.teeth.length > 1 ? 'Teeth' : 'Tooth'}
          value={summary.teeth.map((tooth) => tooth.tooth).join(' · ')}
          mono
        />
        <Chip label="Restoration" value={restorations} />
        <Chip label="Material" value={materials} />
        <Chip label="Shade" value={shades} />
        <Chip label="Version" value={`v${String(summary.version)}`} mono />
      </div>

      <div className="flex items-center gap-2 lg:ml-auto">
        <span
          className={cn(
            'flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-medium whitespace-nowrap',
            days <= 2 ? 'bg-warning-50 text-warning-600' : 'bg-muted text-muted-foreground',
          )}
          title={`Link expires ${new Date(summary.expiresAt).toLocaleString()}`}
        >
          <Clock className="size-3.5" />
          {days === 0 ? 'Expires today' : `Expires in ${String(days)} day${days === 1 ? '' : 's'}`}
        </span>
        {onSharePatient && (
          <Button
            variant="outline"
            size="sm"
            onClick={onSharePatient}
            title={patientLink ? answerTone(patientLink).title : undefined}
          >
            <Share2 />
            {patientLink ? 'Patient link' : 'Share with patient'}
            {patientLink && (
              <span className={cn('size-2 rounded-full', answerTone(patientLink).dot)} />
            )}
          </Button>
        )}
        {onReport && (
          <Button variant="secondary" size="sm" onClick={onReport}>
            <FileDown />
            PDF report
          </Button>
        )}
      </div>
    </header>
  );
}
